
import { useState } from 'react';

export default function SelectionGuide() {
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleConsultationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d3c7ni6enqf2o6v54fog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        alert('お問い合わせありがとうございます。担当者より3営業日以内にご連絡いたします。');
        form.reset();
        setShowConsultationForm(false);
      }
    } catch (error) {
      alert('送信に失敗しました。お電話でのお問い合わせもお受けしております。');
    }
  };

  const palletizerTypes = [
    {
      id: 'mechanical',
      type: '機械式パレタイザー',
      feature: '高処理能力',
      target: '飲料業界向け',
      image: 'https://readdy.ai/api/search-image?query=High-speed%20mechanical%20palletizer%20in%20beverage%20factory%2C%20industrial%20conveyor%20system%20with%20automated%20stacking%20mechanism%2C%20large-scale%20production%20line%2C%20metallic%20equipment&width=600&height=400&seq=mechanical-detailed&orientation=landscape',
      pros: [
        '高速処理（800-1200ケース/時）',
        '大容量対応',
        '耐久性が高い',
        'メンテナンス性良好'
      ],
      cons: [
        '大スペースが必要',
        '初期投資が高額',
        '品種変更に時間要する'
      ],
      applications: ['飲料業界', '食品大量生産', '化学工業'],
      cost: '2,500万円〜',
      space: '100㎡以上',
      capacity: '800-1200ケース/時'
    },
    {
      id: 'articulated',
      type: '多関節ロボット',
      feature: '柔軟性高',
      target: '多品種対応',
      image: 'https://readdy.ai/api/search-image?query=Articulated%20robotic%20arm%20palletizer%20handling%20multiple%20product%20types%2C%20flexible%20automation%20system%2C%20modern%20warehouse%20with%20various%20box%20sizes%2C%20advanced%20robotics%20technology&width=600&height=400&seq=articulated-detailed&orientation=landscape',
      pros: [
        '多品種対応',
        '最大6パレット同時対応',
        '複雑な積み付けパターン可能',
        '設置後の変更容易'
      ],
      cons: [
        '処理速度は中程度',
        'プログラミングが複雑',
        '定期的なキャリブレーション必要'
      ],
      applications: ['多品種製造業', '物流センター', '自動車部品'],
      cost: '2,000万円〜',
      space: '50-80㎡',
      capacity: '400-600ケース/時'
    },
    {
      id: 'cartesian',
      type: '直交ロボット',
      feature: '省スペース・低コスト',
      target: '中小企業向け',
      image: 'https://readdy.ai/api/search-image?query=Compact%20cartesian%20coordinate%20robot%20palletizer%20in%20small%20factory%20space%2C%20space-saving%20automation%20solution%2C%20cost-effective%20industrial%20equipment%2C%20clean%20facility&width=600&height=400&seq=cartesian-detailed&orientation=landscape',
      pros: [
        '省スペース設置',
        '低初期投資',
        'シンプルな構造',
        '操作が簡単'
      ],
      cons: [
        '処理能力は限定的',
        '軽量物中心',
        '積み付けパターンに制約'
      ],
      applications: ['軽量物製造', '中小企業', '食品加工'],
      cost: '1,500万円〜',
      space: '30-50㎡',
      capacity: '200-400ケース/時'
    }
  ];

  const handTypes = [
    {
      type: '挟み込み型ハンド',
      description: '段ボール箱の側面を挟んで持ち上げる方式',
      applications: '段ボール、プラケース',
      image: 'https://readdy.ai/api/search-image?query=Gripping%20type%20robotic%20hand%20holding%20cardboard%20boxes%2C%20mechanical%20clamp%20system%2C%20industrial%20automation%20equipment%2C%20detailed%20mechanical%20components&width=300&height=200&seq=grip-hand&orientation=landscape'
    },
    {
      type: '吸着型ハンド',
      description: '真空吸着で製品を保持する方式',
      applications: '袋物、フィルム包装',
      image: 'https://readdy.ai/api/search-image?query=Vacuum%20suction%20robotic%20hand%20lifting%20bags%20and%20flexible%20packaging%2C%20industrial%20suction%20cups%2C%20automated%20material%20handling%20system&width=300&height=200&seq=suction-hand&orientation=landscape'
    },
    {
      type: '組み合わせ型ハンド',
      description: '挟み込みと吸着の両方を備えた方式',
      applications: '多品種対応',
      image: 'https://readdy.ai/api/search-image?query=Hybrid%20robotic%20hand%20with%20both%20gripping%20and%20suction%20capabilities%2C%20versatile%20automation%20tool%2C%20multi-purpose%20industrial%20equipment&width=300&height=200&seq=hybrid-hand&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-800 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <a href="/" className="text-2xl font-bold hover:text-orange-400 cursor-pointer" style={{ fontFamily: '"Arial", "Helvetica", sans-serif' }}>パレタイザーナビ</a>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <a href="/palletizer-basics" className="hover:text-orange-400 transition-colors cursor-pointer">パレタイザーとは</a>
            <a href="/selection-guide" className="hover:text-orange-400 transition-colors cursor-pointer">機種選定ガイド</a>
            <a href="/decision-criteria" className="hover:text-orange-400 transition-colors cursor-pointer">導入判断基準</a>
            <a href="/case-studies" className="hover:text-orange-400 transition-colors cursor-pointer">導入事例</a>
            <a href="/manufacturers-products" className="hover:text-orange-400 transition-colors cursor-pointer">メーカー・製品</a>
            <a href="/peripheral-systems" className="hover:text-orange-400 transition-colors cursor-pointer">周辺機器</a>
            <a href="/alternatives-comparison" className="hover:text-orange-400 transition-colors cursor-pointer">代替案・比較</a>
          </nav>

          <div className="flex items-center space-x-6">
            <button 
              onClick={() => setShowConsultationForm(true)}
              className="bg-orange-500 hover:bg-orange-600 px-6 py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              見積依頼・お問い合わせ
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white cursor-pointer"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-700">
            <nav className="flex flex-col space-y-4 mt-4">
              <a href="/palletizer-basics" className="hover:text-orange-400 transition-colors cursor-pointer">パレタイザーとは</a>
              <a href="/selection-guide" className="hover:text-orange-400 transition-colors cursor-pointer">機種選定ガイド</a>
              <a href="/decision-criteria" className="hover:text-orange-400 transition-colors cursor-pointer">導入判断基準</a>
              <a href="/case-studies" className="hover:text-orange-400 transition-colors cursor-pointer">導入事例</a>
              <a href="/manufacturers-products" className="hover:text-orange-400 transition-colors cursor-pointer">メーカー・製品</a>
              <a href="/peripheral-systems" className="hover:text-orange-400 transition-colors cursor-pointer">周辺機器</a>
              <a href="/alternatives-comparison" className="hover:text-orange-400 transition-colors cursor-pointer">代替案・比較</a>
              <button 
                onClick={() => {
                  setShowConsultationForm(true);
                  setMobileMenuOpen(false);
                }}
                className="bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors text-left"
              >
                見積依頼・お問い合わせ
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center space-x-2 text-sm text-slate-600">
            <a href="/" className="hover:text-blue-600 cursor-pointer">ホーム</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-slate-800 font-semibold">機種選定ガイド</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6">機種選定ガイド</h1>
          <p className="text-xl text-green-100 mb-8">3つのタイプから最適なパレタイザーを選択</p>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">パレタイザー比較表</h2>
          
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full bg-white shadow-lg rounded-lg overflow-hidden">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="p-4 text-left">項目</th>
                  <th className="p-4 text-center">機械式</th>
                  <th className="p-4 text-center">多関節ロボット</th>
                  <th className="p-4 text-center">直交ロボット</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-4 font-semibold bg-slate-50">処理能力</td>
                  <td className="p-4 text-center">800-1200ケース/時</td>
                  <td className="p-4 text-center">400-600ケース/時</td>
                  <td className="p-4 text-center">200-400ケース/時</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-semibold bg-slate-50">設置面積</td>
                  <td className="p-4 text-center">100㎡以上</td>
                  <td className="p-4 text-center">50-80㎡</td>
                  <td className="p-4 text-center">30-50㎡</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-semibold bg-slate-50">初期投資</td>
                  <td className="p-4 text-center">2,500万円〜</td>
                  <td className="p-4 text-center">2,000万円〜</td>
                  <td className="p-4 text-center">1,500万円〜</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-semibold bg-slate-50">多品種対応</td>
                  <td className="p-4 text-center text-red-600">△</td>
                  <td className="p-4 text-center text-green-600">◎</td>
                  <td className="p-4 text-center text-yellow-600">○</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-semibold bg-slate-50">メンテナンス</td>
                  <td className="p-4 text-center text-green-600">◎</td>
                  <td className="p-4 text-center text-yellow-600">○</td>
                  <td className="p-4 text-center text-green-600">◎</td>
                </tr>
                <tr>
                  <td className="p-4 font-semibold bg-slate-50">適用業界</td>
                  <td className="p-4 text-center">飲料・食品大量生産</td>
                  <td className="p-4 text-center">多品種製造・物流</td>
                  <td className="p-4 text-center">軽量物・中小企業</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-6">
            {palletizerTypes.map((type, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-slate-800 mb-4">{type.type}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-semibold">処理能力:</span>
                    <span>{type.capacity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">設置面積:</span>
                    <span>{type.space}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">初期投資:</span>
                    <span>{type.cost}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Types Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">各タイプの詳細説明</h2>
          <div className="space-y-12">
            {palletizerTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-8">
                  <div>
                    <img 
                      src={type.image}
                      alt={type.type}
                      className="w-full h-80 object-cover"
                    />
                  </div>
                  <div className="p-8">
                    <h3 className="text-3xl font-bold text-slate-800 mb-4">{type.type}</h3>
                    <p className="text-lg text-slate-600 mb-6">{type.feature}</p>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="font-bold text-green-600 mb-2">メリット</h4>
                        <ul className="space-y-1">
                          {type.pros.map((pro, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                              <span className="text-sm text-slate-600">{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-red-600 mb-2">注意点</h4>
                        <ul className="space-y-1">
                          {type.cons.map((con, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-close-line text-red-600 mr-2 mt-1"></i>
                              <span className="text-sm text-slate-600">{con}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-bold text-slate-800 mb-2">主な適用業界</h4>
                      <div className="flex flex-wrap gap-2">
                        {type.applications.map((app, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {app}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hand Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">ハンドの種類と選び方</h2>
          <div className="mb-12 text-center">
            <p className="text-lg text-slate-600 max-w-4xl mx-auto">
              パレタイザーのハンドとは、パレタイザーの先端部分で積載物を掴む部分のことを指します。
              積載物を損壊させない、落とさないことが重要で、適切なグリップ力、摩擦力、せん断力、面圧などのスペックを持つハンドの選定が重要となります。
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                type: '挟み込み型ハンド',
                description: '積載物を挟んで掴むタイプのハンド。製品の大きさや硬さ、重量などによって挟み込み部分の形状や種類を変更するため、基本的にどのような積載物でも対応が可能です。',
                applications: '段ボール、プラケース',
                characteristics: '挟み込んで積載物を離す際の動作が必要になるので、積載物同士に若干のすき間ができます。',
                image: 'https://readdy.ai/api/search-image?query=Gripping%20type%20robotic%20hand%20holding%20cardboard%20boxes%2C%20mechanical%20clamp%20system%20with%20precise%20gripping%20mechanism%2C%20industrial%20automation%20equipment%2C%20detailed%20mechanical%20components%2C%20side%20gripping%20action&width=300&height=200&seq=grip-hand-detailed&orientation=landscape'
              },
              {
                type: '吸着型ハンド',
                description: '真空パッドで製品を掴むタイプになります。真空状態にして積載物を吸着するため、重量や積載物の性質によっては対応できない場合があります。',
                applications: '袋物、フィルム包装',
                characteristics: '柔軟な形状の製品にも対応でき、密着した積み付けが可能です。',
                image: 'https://readdy.ai/api/search-image?query=Vacuum%20suction%20robotic%20hand%20lifting%20bags%20and%20flexible%20packaging%2C%20industrial%20suction%20cups%20with%20vacuum%20system%2C%20automated%20material%20handling%20system%2C%20pneumatic%20gripper%20technology&width=300&height=200&seq=suction-hand-detailed&orientation=landscape'
              },
              {
                type: '組み合わせ型ハンド',
                description: '挟み込みと真空パッドを組み合わせて積載物を掴むタイプのハンドもあります。多様な製品形状に対応できる柔軟性が特徴です。',
                applications: '多品種対応',
                characteristics: '様々な形状・材質の製品を1つのハンドで処理でき、生産効率が向上します。',
                image: 'https://readdy.ai/api/search-image?query=Hybrid%20robotic%20hand%20with%20both%20gripping%20and%20suction%20capabilities%2C%20versatile%20automation%20tool%20combining%20mechanical%20clamps%20and%20vacuum%20pads%2C%20multi-purpose%20industrial%20equipment&width=300&height=200&seq=hybrid-hand-detailed&orientation=landscape'
              }
            ].map((hand, index) => (
              <div key={index} className="bg-slate-50 rounded-lg shadow-lg overflow-hidden">
                <img 
                  src={hand.image}
                  alt={hand.type}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{hand.type}</h3>
                  <p className="text-slate-600 mb-4 text-sm">{hand.description}</p>
                  <div className="bg-blue-50 p-3 rounded-lg mb-4">
                    <span className="text-sm font-semibold text-blue-800">適用製品: </span>
                    <span className="text-sm text-blue-600">{hand.applications}</span>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <span className="text-xs font-semibold text-green-800">特徴: </span>
                    <span className="text-xs text-green-600">{hand.characteristics}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-8">どの機種が最適か診断しませんか？</h2>
          <p className="text-xl mb-8">現場の条件や処理量に合わせて、最適なパレタイザーをご提案いたします</p>
          <div className="flex justify-center space-x-6">
            <a 
              href="/decision-criteria"
              className="bg-white text-green-600 hover:bg-slate-100 px-10 py-4 rounded-lg text-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              導入判断基準を見る
            </a>
            <button 
              onClick={() => setShowConsultationForm(true)}
              className="bg-orange-500 hover:bg-orange-600 px-10 py-4 rounded-lg text-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              専門家に相談する
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Arial", "Helvetica", sans-serif' }}>パレタイザーナビ</h3>
              <p className="text-slate-300 mb-4">パレタイザー専門企業として、製造業・物流業の自動化をサポートします。</p>
              <div className="flex space-x-4">
                <i className="ri-phone-line text-orange-400"></i>
                <span>03-1234-5678</span>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">サービス</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">機械式パレタイザー</a></li>
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">多関節ロボット</a></li>
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">直交ロボット</a></li>
                <li><a href="/peripheral-systems" className="hover:text-orange-400 cursor-pointer">保守・メンテナンス</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">詳細情報</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="/palletizer-basics" className="hover:text-orange-400 cursor-pointer">パレタイザーとは</a></li>
                <li><a href="/decision-criteria" className="hover:text-orange-400 cursor-pointer">導入判断基準</a></li>
                <li><a href="/case-studies" className="hover:text-orange-400 cursor-pointer">導入事例</a></li>
                <li><a href="/manufacturers-products" className="hover:text-orange-400 cursor-pointer">メーカー・製品</a></li>
                <li><a href="/peripheral-systems" className="hover:text-orange-400 cursor-pointer">周辺機器・システム</a></li>
                <li><a href="/alternatives-comparison" className="hover:text-orange-400 cursor-pointer">代替案・比較</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">会社情報</h4>
              <ul className="space-y-2 text-slate-300">
                <li>〒174-0000 東京都板橋区</li>
                <li>設立：1985年</li>
                <li>従業員数：120名</li>
                <li>ISO9001認証取得</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2024 パレタイザーナビ. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-orange-400 hover:text-orange-300 ml-2">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-slate-800">お問い合わせ・見積もり</h3>
                <button 
                  onClick={() => setShowConsultationForm(false)}
                  className="text-slate-500 hover:text-slate-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form id="consultation-form" data-readdy-form onSubmit={handleConsultationSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">会社名 *</label>
                    <input 
                      type="text" 
                      name="company" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">お名前 *</label>
                    <input 
                      type="text" 
                      name="name" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">電話番号 *</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">メールアドレス *</label>
                    <input 
                      type="email" 
                      name="email" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">ご希望の機種タイプ</label>
                  <select name="preferred_type" className="w-full p-3 pr-8 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">選択してください</option>
                    <option value="機械式">機械式パレタイザー</option>
                    <option value="多関節ロボット">多関節ロボット</option>
                    <option value="直交ロボット">直交ロボット</option>
                    <option value="未定">まだ決まっていない</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">現在の課題・ご要望</label>
                  <textarea 
                    name="requirements" 
                    rows={4} 
                    maxLength={500}
                    className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="処理量、設置スペース、予算などご相談ください（500文字以内）"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">希望連絡方法</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input type="radio" name="contact_method" value="電話" className="mr-2" />
                      電話
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="contact_method" value="メール" className="mr-2" />
                      メール
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="contact_method" value="どちらでも" className="mr-2" defaultChecked />
                      どちらでも
                    </label>
                  </div>
                </div>
                <button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
                >
                  送信する
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
